import { toast } from 'react-toastify';
import { getCurrentErrorConfig, getStatusCodeMessage, shouldShowErrorToUser } from '../Config/errorConfig';

class ErrorHandlerService {
  static handleApiError(error, userMessage = null) {
    const config = getCurrentErrorConfig();
    console.error('API Error:', error);
    
    // Check if we should show this error to the user
    if (!shouldShowErrorToUser(error)) {
      this.logError(error, { userMessage, silenced: true });
      return { error, displayMessage: null };
    }
    
    // Determine user-friendly message based on error type
    let displayMessage = userMessage;
    
    if (!displayMessage) {
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        displayMessage = getStatusCodeMessage(status);
      } else if (error.request) {
        // Network error
        displayMessage = config.messages.network;
      } else {
        // Generic error
        displayMessage = config.messages.general;
      }
    }
    
    // Show toast notification
    toast.error(displayMessage, config.toast);
    
    // Log to monitoring service
    this.logError(error, { userMessage, displayMessage });
    
    return { error, displayMessage };
  }

  static handleValidationErrors(validationErrors, generalMessage = 'Please fix the following errors:') {
    const config = getCurrentErrorConfig();
    if (Array.isArray(validationErrors)) {
      validationErrors.forEach(error => {
        toast.error(error.message || error, {
          ...config.toast,
          autoClose: 7000
        });
      });
    } else {
      toast.error(generalMessage, config.toast);
    }
  }

  static showSuccess(message = 'Operation completed successfully') {
    const config = getCurrentErrorConfig();
    toast.success(message, {
      ...config.toast,
      autoClose: 3000
    });
  }

  static showInfo(message) {
    const config = getCurrentErrorConfig();
    toast.info(message, {
      ...config.toast,
      autoClose: 4000
    });
  }

  static showWarning(message) {
    const config = getCurrentErrorConfig();
    toast.warning(message, config.toast);
  }

  static logError(error, additionalInfo = {}) {
    // Enhanced error logging
    const errorLog = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      ...additionalInfo
    };
    
    console.error('Detailed Error Log:', errorLog);
    
    // Send to error monitoring service (Sentry, LogRocket, etc.)
    if (window.Sentry) {
      window.Sentry.captureException(error, {
        extra: additionalInfo,
        tags: {
          component: 'ErrorHandlerService'
        }
      });
    }
    
    // Store in localStorage for debugging (keep last 10 errors)
    try {
      const storedErrors = JSON.parse(localStorage.getItem('taplr_errors') || '[]');
      storedErrors.unshift(errorLog);
      const trimmedErrors = storedErrors.slice(0, 10); // Keep only last 10
      localStorage.setItem('taplr_errors', JSON.stringify(trimmedErrors));
    } catch (e) {
      console.warn('Could not store error in localStorage:', e);
    }
  }

  static getCurrentUserId() {
    try {
      // Try to get user ID from Redux store or localStorage
      const persistedState = JSON.parse(localStorage.getItem('persist:root') || '{}');
      const loginState = JSON.parse(persistedState.Login || '{}');
      return loginState.userid || 'anonymous';
    } catch (e) {
      return 'anonymous';
    }
  }

  static clearStoredErrors() {
    localStorage.removeItem('taplr_errors');
  }

  static getStoredErrors() {
    try {
      return JSON.parse(localStorage.getItem('taplr_errors') || '[]');
    } catch (e) {
      return [];
    }
  }
}

// Global error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  ErrorHandlerService.logError(event.reason, { type: 'unhandledPromiseRejection' });
  
  // Prevent the default browser error page
  event.preventDefault();
  
  // Show user-friendly message
  ErrorHandlerService.handleApiError(event.reason, 'An unexpected error occurred');
});

// Global error handler for JavaScript errors
window.addEventListener('error', (event) => {
  console.error('Global JavaScript error:', event.error);
  ErrorHandlerService.logError(event.error, { 
    type: 'globalJavaScriptError',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno
  });
});

export default ErrorHandlerService;